
/*
 * LUMÉA - CART DRAWER COMPLETO
 * Temporizador + Total + Badges + logos reales + diseño compacto
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();

    this._lumeaEnhanceTimeout = null;
    this._lumeaCountdownInterval = null;
    this._lumeaBarFrame = null;
    this._lumeaBarInitialized = false;

    this.injectLumeaStyles();
    this.startLumeaCountdown();
    this.initializeLumeaFixedBar();

    // Ignora nuestras propias inserciones para evitar
    // actualizaciones visuales repetidas.
    this._lumeaObserver = new MutationObserver((mutations) => {
      const changedByShopify = mutations.some((mutation) => {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE
          ? mutation.target
          : mutation.target.parentElement;

        if (target && target.closest('#LumeaTrustBadges')) {
          return false;
        }

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (node.nodeType === Node.TEXT_NODE) return false;
          if (node.nodeType !== Node.ELEMENT_NODE) return true;
          return !node.matches('#LumeaTrustBadges');
        });
      });

      if (!changedByShopify) return;
      clearTimeout(this._lumeaEnhanceTimeout);
      this._lumeaEnhanceTimeout = setTimeout(() => this.enhanceLumeaCart(), 150);
    });

    this._lumeaObserver.observe(this, {
      childList: true,
      subtree: true
    });

    requestAnimationFrame(() => {
      this.enhanceLumeaCart();
    });
  }

  // SHOPIFY - FUNCIONES ORIGINALES

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.enhanceLumeaCart();
      this.showLumeaFixedBar();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }

        this.positionLumeaFixedBar();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.hideLumeaFixedBar();

    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.enhanceLumeaCart();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // TEMPORIZADOR 10 MINUTOS

  startLumeaCountdown() {
    const STORAGE_KEY = 'lumea_cart_countdown_end';
    const TEN_MINUTES = 10 * 60 * 1000;

    let endTime = parseInt(
      sessionStorage.getItem(STORAGE_KEY),
      10
    );

    if (!endTime || endTime <= Date.now()) {
      endTime = Date.now() + TEN_MINUTES;

      sessionStorage.setItem(
        STORAGE_KEY,
        endTime.toString()
      );
    }

    this._lumeaCountdownEnd = endTime;

    if (this._lumeaCountdownInterval) {
      clearInterval(this._lumeaCountdownInterval);
    }

    const updateTimer = () => {
      let difference =
        this._lumeaCountdownEnd - Date.now();

      if (difference <= 0) {
        this._lumeaCountdownEnd =
          Date.now() + TEN_MINUTES;

        sessionStorage.setItem(
          STORAGE_KEY,
          this._lumeaCountdownEnd.toString()
        );

        difference = TEN_MINUTES;
      }

      const totalSeconds = Math.max(
        0,
        Math.floor(difference / 1000)
      );

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      const timer = document.getElementById(
        'LumeaCartCountdownTime'
      );

      if (timer) {
        timer.textContent =
          `${String(minutes).padStart(2, '0')}:` +
          `${String(seconds).padStart(2, '0')}`;
      }
    };

    this._lumeaUpdateCountdown = updateTimer;
    updateTimer();

    this._lumeaCountdownInterval = setInterval(
      updateTimer,
      1000
    );
  }

  // BARRA FIJA

  initializeLumeaFixedBar() {
    if (this._lumeaBarInitialized) return;
    this._lumeaBarInitialized = true;

    window.addEventListener('resize', () => {
      this.positionLumeaFixedBar();
    });

    window.addEventListener(
      'scroll',
      () => this.positionLumeaFixedBar(),
      { passive: true }
    );
  }

  addLumeaCountdownBar() {
    const checkout = this.querySelector(
      '#CartDrawer-Checkout'
    );

    let bar = document.getElementById(
      'LumeaCartCountdown'
    );

    if (!checkout) {
      if (bar) bar.style.display = 'none';
      return;
    }

    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'LumeaCartCountdown';
      bar.className = 'lumea-cart-countdown';

      bar.setAttribute(
        'aria-label',
        'Tiempo restante del contador del carrito'
      );

      bar.innerHTML = `
        <span
          class="lumea-cart-countdown__icon"
          aria-hidden="true"
        >⏱</span>

        <span class="lumea-cart-countdown__text">
          Tu carrito está reservado por
          <strong id="LumeaCartCountdownTime">
            10:00
          </strong>
        </span>
      `;

      document.body.appendChild(bar);
    }

    this.positionLumeaFixedBar();

    if (this._lumeaUpdateCountdown) {
      this._lumeaUpdateCountdown();
    }
  }

  positionLumeaFixedBar() {
    const bar = document.getElementById(
      'LumeaCartCountdown'
    );

    const drawerInner = this.querySelector(
      '.drawer__inner'
    );

    if (!bar || !drawerInner) return;

    const rect = drawerInner.getBoundingClientRect();

    bar.style.left = `${rect.left}px`;
    bar.style.top = `${rect.top}px`;
    bar.style.width = `${rect.width}px`;

    drawerInner.style.paddingTop = '36px';
  }

  showLumeaFixedBar() {
    this.addLumeaCountdownBar();

    const bar = document.getElementById(
      'LumeaCartCountdown'
    );

    const checkout = this.querySelector(
      '#CartDrawer-Checkout'
    );

    if (!bar || !checkout) return;

    bar.style.display = 'flex';

    if (this._lumeaBarFrame !== null) {
      cancelAnimationFrame(this._lumeaBarFrame);
    }

    const start = performance.now();

    const updatePosition = (now) => {
      if (!this.classList.contains('active')) {
        this._lumeaBarFrame = null;
        return;
      }

      this.positionLumeaFixedBar();

      if (now - start < 600) {
        this._lumeaBarFrame =
          requestAnimationFrame(updatePosition);
      } else {
        this._lumeaBarFrame = null;
      }
    };

    this._lumeaBarFrame =
      requestAnimationFrame(updatePosition);
  }

  hideLumeaFixedBar() {
    if (this._lumeaBarFrame !== null) {
      cancelAnimationFrame(this._lumeaBarFrame);
      this._lumeaBarFrame = null;
    }

    const bar = document.getElementById(
      'LumeaCartCountdown'
    );

    if (bar) bar.style.display = 'none';
  }

  // BADGES DE CONFIANZA Y LOGOS REALES DE PAGO

  addLumeaTrustBadges() {
    // Elimina banners anteriores, si existen.
    this.querySelector('#LumeaCheckoutBanner')?.remove();

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (!checkoutButton) {
      this.querySelector('#LumeaTrustBadges')?.remove();
      return;
    }

    // Se ubica inmediatamente debajo del BLOQUE del botón.
    const anchor = checkoutButton.closest('.cart__ctas') || checkoutButton;
    const existing = this.querySelector('#LumeaTrustBadges');

    if (existing?.dataset.lumeaVersion === '3') {
      if (anchor.nextElementSibling !== existing) {
        anchor.insertAdjacentElement('afterend', existing);
      }
      return;
    }

    // Sustituye cualquier bloque viejo.
    existing?.remove();

    // Seis logos alineados con espacios iguales.
    const paymentMethods = [
      {
        name: 'Visa',
        key: 'visa',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/visa_payment_method_card_icon_142729.webp?v=1791946550'
      },
      {
        name: 'Santander',
        key: 'santander',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/lumea-santander-logo-vector.svg?v=1792455902'
      },
      {
        name: 'Mastercard',
        key: 'mastercard',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/images.png?v=1791946550'
      },
      {
        name: 'Banco de Chile',
        key: 'bch',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/images_1.jpg?v=1792455555'
      },
      {
        name: 'BancoEstado',
        key: 'estado',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/images_1.png?v=1791946550'
      },
      {
        name: 'Mercado Pago',
        key: 'mp',
        img: 'https://cdn.shopify.com/s/files/1/0913/6665/3986/files/images_4.png?v=1792455558'
      }
    ];

    const logos = paymentMethods.map((method) => `
      <span class="lumea-pay-badge lumea-pay-badge--${method.key}" title="${method.name}">
        <img src="${method.img}" alt="${method.name}"
          loading="lazy" decoding="async" width="34" height="22">
      </span>
    `).join('');

    const wrapper = document.createElement('div');
    wrapper.id = 'LumeaTrustBadges';
    wrapper.className = 'lumea-trust';
    wrapper.dataset.lumeaVersion = '3';

    wrapper.innerHTML = `
      <div class="lumea-trust__row">
        <div class="lumea-trust__item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"
            aria-hidden="true">
            <path d="M1 6h13v10H1z"/>
            <path d="M14 10h4l3 3v3h-7z"/>
            <circle cx="5.5" cy="18" r="1.8"/>
            <circle cx="17.5" cy="18" r="1.8"/>
          </svg>
          <span>Envío gratis</span>
        </div>

        <div class="lumea-trust__item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"
            aria-hidden="true">
            <path d="M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <span>60 días de garantía</span>
        </div>
      </div>

      <div class="lumea-trust__payment-section">
        <div class="lumea-trust__payment-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
            aria-hidden="true">
            <rect x="3" y="10" width="18" height="11" rx="2"/>
            <path d="M7 10V7a5 5 0 0 1 10 0v3"/>
          </svg>
          <span>Pago seguro y protegido</span>
        </div>
        <div class="lumea-trust__payments" aria-label="Medios de pago">
          ${logos}
        </div>
      </div>
    `;

    anchor.insertAdjacentElement('afterend', wrapper);
  }

  // CORRECCIÓN SUBTOTAL -> TOTAL
  // NO MODIFICA EL IMPORTE

  setLumeaTotalLabel() {
    const drawer =
      this.querySelector('#CartDrawer') || this;

    const footer = drawer.querySelector(
      '.drawer__footer, .cart-drawer__footer, [class*="drawer-footer"]'
    );

    const roots = footer
      ? [footer, drawer]
      : [drawer];

    for (const root of roots) {
      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT
      );

      const matches = [];
      let node;

      while ((node = walker.nextNode())) {
        if (/^\s*subtotal\s*[:：]?\s*$/i.test(node.nodeValue)) {
          matches.push(node);
        }
      }

      for (const textNode of matches) {
        textNode.nodeValue = textNode.nodeValue.replace(
          /subtotal/i,
          'Total'
        );
      }
    }
  }

  // TEXTO DEL BOTÓN DE PAGO (SIN CAMBIAR SU FUNCIONAMIENTO)

  setLumeaCheckoutButtonLabel() {
    const button = this.querySelector('#CartDrawer-Checkout');
    if (!button) return;

    const label = 'CONTINUAR AL PAGO';
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    let node;
    let updated = false;

    // Respeta iconos y elementos de carga que pueda incluir Shopify.
    while ((node = walker.nextNode())) {
      if (!node.nodeValue.trim()) continue;
      if (node.parentElement?.closest('svg, .loading__spinner, [aria-hidden="true"]')) {
        continue;
      }

      if (!updated) {
        if (node.nodeValue.trim() !== label) node.nodeValue = label;
        updated = true;
      } else {
        node.nodeValue = '';
      }
    }

    if (!updated) {
      button.insertBefore(document.createTextNode(label), button.firstChild);
    }

    button.setAttribute('aria-label', 'Continuar al pago');
  }

  // INICIALIZACIÓN GENERAL

  enhanceLumeaCart() {
    this.addLumeaCountdownBar();
    this.addLumeaTrustBadges();
    this.setLumeaTotalLabel();
    this.setLumeaCheckoutButtonLabel();

    // Por compatibilidad, elimina el antiguo bloque de ahorro si existe.
    this.querySelector('#LumeaCartSavings')?.remove();
  }

  // ESTILOS LUMÉA

  injectLumeaStyles() {
    if (document.getElementById('LumeaCartCustomStyles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'LumeaCartCustomStyles';

    style.textContent = `
      /* =============================================
         1. PRODUCTOS COMPACTOS EN EL CARRITO
         Se mantienen precios, ofertas y controles.
         ============================================= */
      #CartDrawer .cart-items .cart-item {
        display: grid;
        grid-template-columns: 74px minmax(0, 1fr) auto !important;
        grid-template-rows: auto auto !important;
        column-gap: 10px !important;
        row-gap: 7px !important;
        padding: 10px 0 12px !important;
        margin: 0 !important;
        align-items: start;
        border-bottom: 1px solid rgba(73, 58, 53, 0.09);
      }

      #CartDrawer .cart-items .cart-item:last-child {
        border-bottom: none;
      }

      #CartDrawer .cart-item > td {
        min-width: 0;
        padding: 0 !important;
      }

      #CartDrawer .cart-item__media {
        grid-column: 1 !important;
        grid-row: 1 / 3 !important;
        width: 74px !important;
        max-width: 74px !important;
      }

      #CartDrawer .cart-item__image-container {
        width: 100% !important;
        max-width: 100% !important;
      }

      #CartDrawer .cart-item__image {
        display: block;
        width: 100% !important;
        max-width: 100% !important;
        height: auto;
        object-fit: contain;
      }

      #CartDrawer .cart-item__details {
        grid-column: 2 !important;
        grid-row: 1 !important;
        min-width: 0;
      }

      #CartDrawer .cart-item__name {
        display: block;
        margin: 0 0 5px !important;
        font-size: 14px !important;
        line-height: 1.25 !important;
        overflow-wrap: anywhere;
      }

      #CartDrawer .cart-item__details .product-option,
      #CartDrawer .cart-item__details .discounts,
      #CartDrawer .cart-item__details .cart-item__discounted-prices,
      #CartDrawer .cart-item__details .cart-item__price-wrapper {
        margin-top: 3px !important;
        margin-bottom: 3px !important;
        font-size: 12px !important;
        line-height: 1.3 !important;
      }

      #CartDrawer .cart-item__details .price,
      #CartDrawer .cart-item__details .price * {
        font-size: 12px !important;
      }

      #CartDrawer .cart-item__totals {
        grid-column: 3 !important;
        grid-row: 1 !important;
        min-width: 0;
        margin: 0 !important;
        text-align: right;
      }

      #CartDrawer .cart-item__totals .price,
      #CartDrawer .cart-item__totals .price * {
        font-size: 13px !important;
        line-height: 1.25 !important;
      }

      #CartDrawer .cart-item__quantity {
        grid-column: 2 / 4 !important;
        grid-row: 2 !important;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        min-width: 0;
        padding: 0 !important;
        margin: 0 !important;
      }

      #CartDrawer .cart-item__quantity .quantity {
        width: 120px !important;
        min-height: 36px !important;
        height: 36px !important;
      }

      #CartDrawer .cart-item__quantity .quantity__button {
        width: 36px !important;
        min-width: 36px !important;
        height: 36px !important;
      }

      #CartDrawer .cart-item__quantity .quantity__input {
        min-width: 30px;
        height: 36px !important;
        font-size: 13px !important;
      }

      #CartDrawer .cart-item__quantity cart-remove-button,
      #CartDrawer .cart-item__quantity .cart-remove-button {
        margin: 0 !important;
      }

      #CartDrawer .cart-item__quantity cart-remove-button .button {
        min-width: 36px !important;
        min-height: 36px !important;
        padding: 5px !important;
      }

      /* =============================================
         2. BOTÓN MÁS DELGADO, TEXTO EN UNA LÍNEA
         ============================================= */
      #CartDrawer #CartDrawer-Checkout {
        width: 100%;
        min-height: 44px !important;
        height: 44px !important;
        padding: 0 12px !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        line-height: 1.1 !important;
        letter-spacing: 0.045em !important;
        white-space: nowrap !important;
        text-align: center;
      }

      #CartDrawer #CartDrawer-Checkout span:not(.loading__spinner) {
        white-space: nowrap;
      }

      #LumeaCartCountdown {
        position: fixed !important;
        z-index: 10000 !important;
        display: none;
        align-items: center;
        justify-content: center;
        gap: 6px;
        box-sizing: border-box;
        height: 36px;
        min-height: 36px;
        padding: 7px 12px;
        background: #F5ECE7;
        color: #392F2C;
        border-bottom: 1px solid rgba(57,47,44,0.10);
        font-size: 12px;
        font-weight: 400;
        line-height: 1.3;
        letter-spacing: 0.01em;
        text-align: center;
        box-shadow: 0 2px 5px rgba(57,47,44,0.04);
      }

      #LumeaCartCountdown .lumea-cart-countdown__icon {
        font-size: 14px;
        line-height: 1;
        flex-shrink: 0;
      }

      #LumeaCartCountdown .lumea-cart-countdown__text {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 5px;
      }

      #LumeaCartCountdownTime {
        min-width: 38px;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        color: #76554A;
      }

      #CartDrawer .lumea-trust {
        box-sizing: border-box;
        width: 100%;
        margin: 9px 0 3px;
        font-family: inherit;
      }

      #CartDrawer .lumea-trust *,
      #CartDrawer .lumea-trust *::before,
      #CartDrawer .lumea-trust *::after {
        box-sizing: border-box;
      }

      #CartDrawer .lumea-trust__row {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
      }

      #CartDrawer .lumea-trust__item {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        min-width: 0;
        min-height: 34px;
        padding: 6px 5px;
        background: #F5ECE7;
        border: 1px solid #EADDD6;
        border-radius: 7px;
        color: #493A35;
        font-size: 10.5px;
        font-weight: 600;
        line-height: 1.3;
        text-align: center;
      }

      #CartDrawer .lumea-trust__item svg {
        display: block;
        width: 15px;
        height: 15px;
        flex-shrink: 0;
        color: #76554A;
      }

      #CartDrawer .lumea-trust__payment-section {
        margin-top: 14px;
        padding-top: 12px;
        border-top: 1px solid #F0E9E5;
        text-align: center;
      }

      #CartDrawer .lumea-trust__payment-title {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-bottom: 10px;
        color: #74675F;
        font-size: 11px;
        font-weight: 500;
        line-height: 1.4;
      }

      #CartDrawer .lumea-trust__payment-title svg {
        display: block;
        width: 14px;
        height: 14px;
        flex-shrink: 0;
      }

      /* Logos de pago: 6 espacios iguales y fila centrada. */
      #CartDrawer .lumea-trust__payments {
        display: grid;
        grid-template-columns: repeat(6, 34px);
        justify-content: center;
        justify-items: center;
        align-items: center;
        column-gap: 6px;
        width: 100%;
        margin: 0;
        padding: 0;
      }

      #CartDrawer .lumea-pay-badge {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        min-width: 0;
        height: 22px;
        padding: 0;
        margin: 0;
        background: transparent;
        border: 0;
        border-radius: 2px;
        box-shadow: none;
        overflow: hidden;
      }

      #CartDrawer .lumea-pay-badge img {
        display: block;
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        margin: 0;
        padding: 0;
        object-fit: contain;
        object-position: center;
      }

      /* Ajusta el logo horizontal de Santander a un isotipo compacto. */
      #CartDrawer .lumea-pay-badge--santander img {
        position: absolute;
        top: 0;
        left: 4px;
        width: auto;
        max-width: none;
        height: 100%;
        object-fit: fill;
        clip-path: inset(0 calc(100% - 26px) 0 0);
      }

      @media screen and (max-width: 749px) {
        #LumeaCartCountdown {
          font-size: 11.5px;
          padding: 7px 9px;
          gap: 5px;
        }

        #LumeaCartCountdown .lumea-cart-countdown__icon {
          font-size: 13px;
        }

        #CartDrawer .cart-items .cart-item {
          grid-template-columns: 68px minmax(0, 1fr) auto !important;
          column-gap: 9px !important;
          row-gap: 6px !important;
          padding: 8px 0 10px !important;
        }
        #CartDrawer .cart-item__media {
          width: 68px !important;
          max-width: 68px !important;
        }
        #CartDrawer .cart-item__name { font-size: 13px !important; }
        #CartDrawer .cart-item__totals .price,
        #CartDrawer .cart-item__totals .price * { font-size: 12px !important; }
        #CartDrawer #CartDrawer-Checkout {
          min-height: 43px !important;
          height: 43px !important;
          font-size: 12px !important;
          letter-spacing: 0.03em !important;
        }
        #CartDrawer .lumea-trust__row { gap: 6px; }
        #CartDrawer .lumea-trust__item {
          min-height: 32px;
          font-size: 10px;
          padding: 6px 3px;
          gap: 4px;
        }
        #CartDrawer .lumea-trust__item svg { width: 14px; height: 14px; }
        #CartDrawer .lumea-trust__payment-section { margin-top: 12px; }
        #CartDrawer .lumea-trust__payments {
          grid-template-columns: repeat(6, 32px);
          column-gap: 6px;
        }
        #CartDrawer .lumea-pay-badge { width: 32px; height: 20px; }
        #CartDrawer .lumea-pay-badge--santander img {
          left: 3px;
          clip-path: inset(0 calc(100% - 24px) 0 0);
        }
      }

      @media screen and (max-width: 350px) {
        #CartDrawer .cart-items .cart-item {
          grid-template-columns: 58px minmax(0, 1fr) auto !important;
          column-gap: 7px !important;
        }
        #CartDrawer .cart-item__media {
          width: 58px !important;
          max-width: 58px !important;
        }
        #CartDrawer .cart-item__name { font-size: 12px !important; }
        #CartDrawer .lumea-trust__item { font-size: 9px; }
        #CartDrawer .lumea-trust__payments {
          grid-template-columns: repeat(6, 29px);
          column-gap: 5px;
        }
        #CartDrawer .lumea-pay-badge { width: 29px; height: 18px; }
        #CartDrawer .lumea-pay-badge--santander img {
          left: 2px;
          clip-path: inset(0 calc(100% - 22px) 0 0);
        }
      }
    `;

    document.head.appendChild(style);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// CART DRAWER ITEMS

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
